import dayjs from "dayjs";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Reports from "../../common/reports/Reports";
import Autocomplete from "../../framework/Autocomplete";
import Button from "../../framework/Button";
import GlobalContext from "../../framework/GlobalContext";
import Shortcut from "../../framework/Shortcut";
import { displayToast } from "../../framework/toast/toastUtils";
import { addDoctorCommission } from "../../services/doctor-commission";
import DoctorCommissionProgramDetails from "./doctorCommissionProgramDetails";
import classes from "./DoctorCommissionProgramReport.module.css";
import DoctorCommissionProgramReportFilter from "./DoctorCommissionProgramReportFilter";
import DoctorCommissionProgramReportSummary from "./DoctorCommissionProgramReportSummary";
import { DOCTOR_COMMISSION_TAB } from "../../framework/tab/tabUtilsConstants";
const DoctorCommissionProgramReport = () => {
  const [onFilterShow, setOnFilterShow] = useState(false);
  const [onColumnShow, setOnColumnShow] = useState(false);
  const [rowLevelCTAs, setRowLevelCTAs] = useState([]);
  const [filterParams, setFilterParams] = useState(null);
  const [filterStates, setFilterStates] = useState(null);
  const [currentRowData, setCurrentRowData] = useState(null);
  const [
    showDoctorCommissionProgramDetails,
    setShowDoctorCommissionProgramDetails,
  ] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { currentComponent, setCurrentComponent, setAddNewTab } =
    useContext(GlobalContext);
  const reportRef = useRef();
  const refreshRef = useRef();
  const editRef = useRef();
  const enterRef = useRef();
  const inactivateBtnRef = useRef();
  const exportRef = useRef();

  const refreshData = useCallback((filterData = {}) => {
    reportRef.current.applyFilters(filterData);
  }, []);

  const applyFilterHandler = useCallback((params, filterStates) => {
    setFilterParams(params);
    setFilterStates(filterStates);
  }, []);
  const quickExportHandler = () => {
    reportRef?.current?.quickExport({
      fileName: "DoctorCommissionProgramReport",
    });
  };
  const closeDoctorCommissionProgramDetails = useCallback(() => {
    setShowDoctorCommissionProgramDetails(false);
    setCurrentComponent("grid_reports");
  }, [setCurrentComponent]);

  const handleIsActive = useCallback(async () => {
    const programDetails = currentRowData?.data;
    const doctorCommissionDetails = programDetails.doctorCommissionDetails.map(
      (details) => ({
        ...details,
        deleted: programDetails.deleted ? false : true,
      })
    );
    const payload = {
      ...programDetails,
      deleted: programDetails?.deleted ? false : true,
      doctorCommissionDetails: doctorCommissionDetails,
    };
    await addDoctorCommission(payload).then((resp) => {
      if (resp?.status === "success") {
        displayToast({
          type: "success",
          title: programDetails?.deleted
            ? "Doctor Commission Program Activated!"
            : "Doctor Commission Program Inactivated!",
        });
        refreshData();
      }
    });
  }, [currentRowData?.data, refreshData]);

  const listener = useCallback((e) => {
    const key = e.key?.toLowerCase();
    const ctrlKey = e.ctrlKey;
    const altKey = e.altKey;
    if (altKey && key === "r") {
      refreshRef.current.click();
      return;
    }
    if (altKey && key === "e") {
      exportRef.current.click();
      return;
    }
    if (ctrlKey && key === "e") {
      editRef.current.click();
      return;
    }
    if (ctrlKey && key === "i") {
      enterRef.current?.click();
      return;
    }
    if (key === "enter") {
      enterRef.current.click();
      return;
    }
    if (altKey && key === "l") {
      inactivateBtnRef.current.click();
      return;
    }
  }, []);

  const editHandler = useCallback(() => {
    setAddNewTab({
        component: DOCTOR_COMMISSION_TAB,
      props: { doctorCommissionId: currentRowData.data.doctorCommissionId },
    });
  }, [currentRowData, setAddNewTab]);

  useEffect(() => {
    const allRowCTAs = [
      <Button
        ref={editRef}
        startIcon={
          <>
            <Shortcut keys={["Ctrl", "E"]} />
          </>
        }
        label="Edit"
        onClick={() => {
          editHandler();
        }}
        isAccessible={
          currentRowData &&
          !dayjs(currentRowData?.data?.endDate).isBefore(dayjs(), "day")
        }
      />,
      <Button
        ref={enterRef}
        startIcon={
          <>
            <Shortcut keys={["Enter"]} />/<Shortcut keys={["Ctrl", "I"]} />
          </>
        }
        label="info"
        onClick={() => {
          setShowDoctorCommissionProgramDetails(true);
        }}
        isAccessible={currentRowData}
      />,
      <Button
        ref={inactivateBtnRef}
        startIcon={
          <>
            <Shortcut keys={["Alt", "L"]} />
          </>
        }
        label={currentRowData?.data?.deleted ? "Activate" : "Inactivate"}
        onClick={() => {
          handleIsActive();
        }}
        isAccessible={
          currentRowData &&
          dayjs(currentRowData?.data?.endDate).isAfter(dayjs(), "day")
        }
        sx={{
          width: "10.3125rem",
        }}
      />,
    ];
    setRowLevelCTAs(allRowCTAs);
  }, [currentRowData, editHandler, handleIsActive]);

  useEffect(() => {
    setCurrentComponent("grid_reports");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (!onFilterShow && !onColumnShow && currentComponent === "grid_reports") {
      document.addEventListener("keyup", listener, false);
    }
    return () => {
      document.removeEventListener("keyup", listener, false);
    };
  }, [currentComponent, listener, onColumnShow, onFilterShow]);
  useEffect(() => {
    refreshData({ isFilterChanged: true, ...filterStates });
  }, [refreshData, filterStates]);
  return (
    <div className={classes["doctor-commission-program-report-container"]}>
      <Reports
        identifier="doctorCommissionProgramReport"
        reportRef={reportRef}
        rowLevelCTAs={rowLevelCTAs}
        filterStates={filterStates}
        filterParams={filterParams}
        onFilterShowClick={setOnFilterShow}
        onColumnShowClick={setOnColumnShow}
        currentRow={(data) => {
          setCurrentRowData(data);
        }}
        onCellClicked={(params) => {
          if (params.column.colId === "doctorCommissionId") {
            if (params.data.doctorCommissionId)
              setShowDoctorCommissionProgramDetails(true);
          }
        }}
        filters={
          <DoctorCommissionProgramReportFilter
            reportRef={reportRef}
            filters={(filters) => {
              setFilterStates(filters);
            }}
            onFilterShow={onFilterShow}
            refreshData={refreshData}
            applyFilters={applyFilterHandler}
          />
        }
      />
      <Autocomplete
        show={showDoctorCommissionProgramDetails}
        height="60%"
        closeAutoComplete={closeDoctorCommissionProgramDetails}
      >
        <DoctorCommissionProgramDetails
          doctorCommissionId={currentRowData?.data?.doctorCommissionId}
          onEscape={closeDoctorCommissionProgramDetails}
        />
      </Autocomplete>
      <DoctorCommissionProgramReportSummary
        refreshData={refreshData}
        refreshRef={refreshRef}
        quickExport={quickExportHandler}
        exportRef={exportRef}
      />
    </div>
  );
};

export default DoctorCommissionProgramReport;
